<template>
  <div>
    <v-card outlined class="mb-4">
      <v-card-subtitle class="font-weight-bold pb-0">Planos</v-card-subtitle>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="5">
            <app-select @change="select()" :items="plans" v-model="searchParams.from_plan_id" :item-text="planText"
              item-value="id" label="Plano de origem" outlined clearable />
            <div v-if="fromPlan" class="mt-2">
              <v-chip v-for="chip in planChips(fromPlan)" :key="chip" small outlined class="mr-2 mb-1">{{ chip }}</v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="2" class="text-center py-0">
            <v-icon large color="primary" :class="{ 'rotate-90': $vuetify.breakpoint.smAndDown }">
              mdi-arrow-right-bold
            </v-icon>
          </v-col>
          <v-col cols="12" md="5">
            <app-select :items="targetPlans" v-model="to_plan_id" :item-text="planText" item-value="id"
              label="Plano de destino" outlined clearable />
            <div v-if="toPlan" class="mt-2">
              <v-chip v-for="chip in planChips(toPlan)" :key="chip" small outlined class="mr-2 mb-1">{{ chip }}</v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-4">
      <v-card-subtitle class="font-weight-bold pb-0">Filtros</v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <app-select @change="select()" label="Status da assinatura" :items="subscriptionsStatus" small-chips
              deletable-chips multiple outlined v-model="searchParams.status" />
          </v-col>
          <v-col cols="12" md="6">
            <app-select @change="select()" label="Método de pagamento" :items="paymentVendors" small-chips
              deletable-chips multiple outlined v-model="searchParams.vendor" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert v-if="warnings.length" type="warning" text dense class="mb-4">
      <div v-for="(warning, index) in warnings" :key="index">{{ warning }}</div>
    </v-alert>

    <v-card outlined>
      <v-card-subtitle class="d-flex align-center flex-wrap font-weight-bold">
        Assinaturas
        <v-spacer />
        <template v-if="searchParams.from_plan_id">
          <span class="font-weight-regular mr-4">
            <template v-if="selected.length">{{ selected.length }} selecionada(s)</template>
            <template v-else>Todas as {{ subscriptions.total || 0 }} do filtro serão migradas</template>
          </span>
          <v-btn color="primary" :disabled="!canMigrate" @click="confirmDialog = true">
            <v-icon left>mdi-swap-horizontal</v-icon>
            Migrar {{ migrateCount }}
          </v-btn>
        </template>
      </v-card-subtitle>

      <v-card-text v-if="!searchParams.from_plan_id" class="text-center py-12 text--secondary">
        <v-icon x-large class="mb-2">mdi-swap-horizontal-circle-outline</v-icon>
        <div>Selecione o plano de origem para listar as assinaturas.</div>
      </v-card-text>

      <v-card-text v-else>
        <app-pagination @click="select($event)" :data="subscriptions">
          <v-data-table dense show-select v-model="selected" :headers="headers" :items="subscriptions.data"
            hide-default-footer disable-sort :items-per-page="-1">
            <template v-slot:[`item.company`]="{ item }">
              {{ item.company.name }} <small class="text--secondary">#{{ item.company_id }}</small>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small label :color="statusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
            </template>
            <template v-slot:[`item.vendor`]="{ item }">
              {{ vendorText(item.vendor) }}
            </template>
            <template v-slot:[`item.ends_at`]="{ item }">
              {{ $format.dateBr(item.ends_at) }}
            </template>
          </v-data-table>
        </app-pagination>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirmDialog" max-width="520px">
      <v-card>
        <v-card-title>Confirmar migração</v-card-title>
        <v-card-text>
          <div class="d-flex align-center justify-space-between mb-4">
            <div>
              <div class="text--secondary">De</div>
              <b>{{ fromPlan && fromPlan.name }}</b>
              <div>{{ fromPlan && $format.moneyBr(fromPlan.value) }}</div>
            </div>
            <v-icon color="primary">mdi-arrow-right-bold</v-icon>
            <div class="text-right">
              <div class="text--secondary">Para</div>
              <b>{{ toPlan && toPlan.name }}</b>
              <div>{{ toPlan && $format.moneyBr(toPlan.value) }}</div>
            </div>
          </div>
          <p>
            <b>{{ migrateCount }}</b> assinatura(s) serão migradas. As próximas renovações usarão o valor e o
            intervalo do novo plano. Faturas já emitidas não são alteradas.
          </p>
          <v-alert v-if="warnings.length" type="warning" text dense class="mb-0">
            <div v-for="(warning, index) in warnings" :key="index">{{ warning }}</div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <app-back-btn @click="confirmDialog = false" />
          <v-spacer />
          <app-confirm-btn @click="handleMigrate()" />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const intervals = { day: ["dia", "dias"], month: ["mês", "meses"], year: ["ano", "anos"] };

const intervalText = (plan) => {
  const [singular, plural] = intervals[plan.interval] || [plan.interval, plan.interval];
  return plan.interval_count > 1 ? `${plan.interval_count} ${plural}` : singular;
};

export default {
  data() {
    return {
      paymentVendors: [
        { text: "Efi", value: "efi" },
        { text: "Stripe", value: "stripe" },
        { text: "Pagar.Me", value: "pagarme" },
        { text: "PagSeguro", value: "pagseguro" },
        { text: "PIX", value: "pix" },
      ],

      subscriptionsStatus: [
        { value: "paid", text: "paid" },
        { value: "active", text: "active" },
        { value: "pending_payment", text: "pending_payment" },
        { value: "waiting_payment", text: "waiting_payment" },
        { value: "processing", text: "processing" },
        { value: "past_due", text: "past_due" },
        { value: "unpaid", text: "unpaid" },
        { value: "ended", text: "ended" },
        { value: "canceled", text: "canceled" },
      ],

      headers: [
        { text: "Assinatura", value: "id" },
        { text: "Empresa", value: "company" },
        { text: "Status", value: "status" },
        { text: "Vendor", value: "vendor" },
        { text: "Fim", value: "ends_at" },
      ],

      plans: [],

      subscriptions: {},

      selected: [],

      to_plan_id: null,

      confirmDialog: false,

      searchParams: {
        from_plan_id: null,
        status: ["paid", "active", "pending_payment", "waiting_payment", "past_due"],
        vendor: ["efi"],
        page: 1,
      },
    };
  },

  computed: {
    fromPlan() {
      return this.plans.find((plan) => plan.id == this.searchParams.from_plan_id);
    },

    toPlan() {
      return this.plans.find((plan) => plan.id == this.to_plan_id);
    },

    targetPlans() {
      return this.plans.filter((plan) => plan.id != this.searchParams.from_plan_id);
    },

    migrateCount() {
      return this.selected.length || this.subscriptions.total || 0;
    },

    canMigrate() {
      return this.fromPlan && this.toPlan && this.migrateCount > 0;
    },

    warnings() {
      let warnings = [];

      const externalVendors = (this.searchParams.vendor || []).filter((vendor) => vendor != "efi");

      if (externalVendors.length) {
        warnings.push(
          `A cobrança de ${externalVendors.join(", ")} é feita no gateway; a migração altera apenas o plano no sistema.`
        );
      }

      if (this.fromPlan && this.toPlan) {
        if (this.fromPlan.type != this.toPlan.type) {
          warnings.push(`Os planos têm tipos diferentes (${this.fromPlan.type} → ${this.toPlan.type}).`);
        }

        if (
          this.fromPlan.interval != this.toPlan.interval ||
          this.fromPlan.interval_count != this.toPlan.interval_count
        ) {
          warnings.push(
            `Os planos têm intervalos diferentes (${intervalText(this.fromPlan)} → ${intervalText(this.toPlan)}).`
          );
        }
      }

      return warnings;
    },
  },

  mounted() {
    this.getPlans();
  },

  methods: {
    getPlans() {
      this.$http.index("subscription/plans").then((response) => {
        this.plans = response.plans;
      });
    },

    select(page) {
      this.selected = [];
      this.searchParams.page = page;

      if (!this.searchParams.from_plan_id) {
        this.subscriptions = {};
        return;
      }

      this.$loading.start();
      this.$http
        .index("subscription/admin/plan-migration", this.searchParams)
        .then((response) => {
          this.$loading.finish();
          this.subscriptions = response.subscriptions;
        })
        .catch((error) => this.$loading.finish());
    },

    handleMigrate() {
      this.confirmDialog = false;
      this.$loading.start();
      this.$http
        .store("subscription/admin/plan-migration", {
          ...this.searchParams,
          to_plan_id: this.to_plan_id,
          subscription_ids: this.selected.map((subscription) => subscription.id),
        })
        .then((response) => {
          this.$loading.finish();
          this.$snackbar({
            message: `${response.subscriptions_updated} assinatura(s) migrada(s) com sucesso`,
            color: "success",
            timeout: 3000,
            showClose: false,
          });
          this.select();
        })
        .catch((error) => this.$loading.finish());
    },

    statusColor(status) {
      if (["paid", "active"].includes(status)) return "success";
      if (["pending_payment", "waiting_payment", "processing"].includes(status)) return "warning";
      if (["past_due", "unpaid"].includes(status)) return "error";
      return "grey";
    },

    vendorText(vendor) {
      const item = this.paymentVendors.find((item) => item.value == vendor);
      return item ? item.text : vendor;
    },

    planChips(plan) {
      return [
        `${this.$format.moneyBr(plan.value)} / ${intervalText(plan)}`,
        plan.type == "clinic" ? "Clínica" : "Individual",
        plan.vendor || "sem vendor",
        plan.status,
      ];
    },

    planText(plan) {
      return `#${plan.id} ${plan.name} — ${this.$format.moneyBr(plan.value)}`;
    },
  },
};
</script>

<style scoped>
.rotate-90 {
  transform: rotate(90deg);
}
</style>
